import type { DomainId } from "./domains";

// =============================================================================
// DOMAIN <-> URL MAPPING
// =============================================================================
// Every domain has a canonical URL path. The (home) route group owns these
// routes; HomeShell reads the pathname to drive state and writes new paths
// via window.history when spatial transitions fire.

export const DOMAIN_TO_PATH: Record<DomainId, string> = {
  projects: "/work",
  contact: "/contact",
  manifesto: "/about",
  resume: "/resume",
  blog: "/blog",
};

// Inverse lookup for static domain paths. "/" also maps to projects so the
// bare root URL stays a valid entry point.
export const PATH_TO_DOMAIN: Record<string, DomainId> = {
  "/": "projects",
  "/work": "projects",
  "/contact": "contact",
  "/about": "manifesto",
  "/resume": "resume",
  "/blog": "blog",
};

// Reserved paths we never want to interpret as a project slug.
const RESERVED_PATHS = new Set<string>([
  ...Object.keys(PATH_TO_DOMAIN),
  "/api",
  "/ds",
]);

// Parse a pathname into a domain + optional project/blog slug. Returns null for
// paths we don't own (so the caller can ignore them).
export function parsePath(pathname: string): {
  domain: DomainId;
  projectSlug: string | null;
  blogSlug: string | null;
} | null {
  const domain = PATH_TO_DOMAIN[pathname];
  if (domain) return { domain, projectSlug: null, blogSlug: null };

  // /blog/:slug — prose post deep link
  const blogMatch = pathname.match(/^\/blog\/([a-z0-9-]+)$/);
  if (blogMatch) return { domain: "blog", projectSlug: null, blogSlug: blogMatch[1] };

  // /:slug form — only valid for single-segment kebab paths that aren't reserved.
  const match = pathname.match(/^\/([a-z0-9-]+)$/);
  if (match && !RESERVED_PATHS.has(pathname)) {
    return { domain: "projects", projectSlug: match[1], blogSlug: null };
  }

  return null;
}
